import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ILandingPageMerchandise,
  ILink,
  IPanelLink,
  isIPanelLink,
  ITileLink,
  MerchandiseService,
} from '@epicuro-next/data/merchandise';
import {
  DynamicItemsConfigEntry,
  DynamicItemsConfigWithArguments,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { ResolveConfigService } from '@epicuro-next/platform/dynamic-components';
import { EcommercePromotionSource } from '@epicuro-next/platform/tracking';
import {
  selectFilterBasedLandingPage,
  selectMerchandisingDynamicPanelConfigs,
} from '@epicuro-next/state/home-state';
import { selectActiveFilters } from '@epicuro-next/state/p-basket-state';
import { selectRouteParams } from '@epicuro-next/state/router-store';
import {
  selectAccountId,
  selectSiteId,
  selectSubSiteId,
} from '@epicuro-next/state/session-state';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { LandingPageComponentStore } from '../store/landing-page.component.store';

export interface Data<T> {
  content?: T;
  loading?: boolean;
  error?: boolean;
}

@Component({
  selector: 'epc-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LandingPageComponentStore],
  standalone: false,
})
export class LandingPageComponent implements OnInit, OnDestroy {
  public landingPageContent$: Observable<Data<ILandingPageMerchandise>>;
  public destroy$$ = new Subject<void>();
  public promotionSource = EcommercePromotionSource;
  public tilesIndexAddition = 1;

  private filterBasedLandingPage$ = this.store.pipe(
    select(selectFilterBasedLandingPage),
    distinctUntilChanged(),
  );

  private handleFilterBasedLandingPageSlotSuffix$ =
    this.filterBasedLandingPage$.pipe(
      switchMap((filterBasedLandingPage) => {
        if (!filterBasedLandingPage) return of('');
        return this.store.pipe(
          select(selectActiveFilters),
          map((filters) => {
            return (
              filters.find(
                (filterValue) => filterValue.fieldId === filterBasedLandingPage,
              )?.value ?? ''
            );
          }),
        );
      }),
      distinctUntilChanged(),
    );

  private merchandiseParams$: Observable<{
    siteId: string | null;
    subSiteId: string | null;
    accountId: string;
    landingPageSuffix: string;
    version?: string;
  }>;

  private readonly selectMerchandisingDynamicPanelConfigs$ = this.store.pipe(
    select(selectMerchandisingDynamicPanelConfigs),
  );

  constructor(
    private readonly merchandiseService: MerchandiseService,
    private readonly store: Store,
    private readonly resolveConfigService: ResolveConfigService,
    private readonly componentStore: LandingPageComponentStore,
  ) {}

  public ngOnInit() {
    this.merchandiseParams$ = combineLatest([
      this.store.pipe(select(selectSiteId)),
      this.store.pipe(select(selectSubSiteId)),
      this.store.pipe(select(selectAccountId), filter<string>(Boolean)),
      this.handleFilterBasedLandingPageSlotSuffix$,
      this.store.pipe(
        select(selectRouteParams),
        map((routeParams) => routeParams?.homeId),
        distinctUntilChanged(),
      ),
    ]).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      map(([siteId, subSiteId, accountId, landingPageSuffix, version]) => {
        return {
          siteId,
          subSiteId,
          accountId,
          landingPageSuffix,
          version,
        };
      }),
    );

    this.setupLandingPageContent();
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  private setupLandingPageContent() {
    this.landingPageContent$ = combineLatest([
      this.merchandiseParams$,
      this.selectMerchandisingDynamicPanelConfigs$,
    ]).pipe(
      switchMap(
        ([
          { siteId, subSiteId, accountId, landingPageSuffix, version },
          merchandisingDynamicPanelConfigs,
        ]) =>
          this.merchandiseService
            .getLandingPage(
              siteId,
              subSiteId,
              accountId,
              landingPageSuffix,
              version,
            )
            .pipe(
              tap((content) => {
                this.tilesIndexAddition =
                  (content.banner?.links.length ?? 0) + 1;
              }),
              map((content) => ({ content })),
              tap((landingPageMerchandise) => {
                if (landingPageMerchandise.content.rows) {
                  this.resolveDynamicPanelContentForPanelTiles(
                    landingPageMerchandise,
                    merchandisingDynamicPanelConfigs,
                  );
                }
              }),
              catchError(() => of({ error: true })),
            ),
      ),
      startWith({ loading: true }),
      takeUntil(this.destroy$$),
    );
  }

  private resolveDynamicPanelContentForPanelTiles(
    landingPageMerchandise: { content: ILandingPageMerchandise },
    merchandisingDynamicPanelConfigs: {
      [key: string]:
        | DynamicItemsConfigWithArguments
        | DynamicItemsConfigEntry[];
    },
  ) {
    const tilesWithDynamicPanels = landingPageMerchandise.content.rows
      .reduce((acc, row) => {
        return acc.concat(row.tiles);
      }, [] as ITileLink[])
      .filter((tile) => isIPanelLink(tile)) as IPanelLink[];

    for (const tile of tilesWithDynamicPanels) {
      const panelName = tile.params.panelName;
      const dynamicPanelConfig = merchandisingDynamicPanelConfigs[panelName];
      if (dynamicPanelConfig === undefined) {
        console.error(
          `For landing-page a tile is configured to show a panel with name '${panelName}' but no panel with this name is configured.`,
        );
      }

      let fallbackPanelConfig:
        | DynamicItemsConfigWithArguments
        | DynamicItemsConfigEntry[]
        | undefined;
      if (tile.params.fallbackPanel !== undefined) {
        const fallbackPanelName = tile.params.fallbackPanel.panelName;
        fallbackPanelConfig =
          merchandisingDynamicPanelConfigs[fallbackPanelName];
      }

      tile.panelContent$ =
        this.resolveConfigService.resolveDynamicPanelFromPanelParams(
          tile.params,
          dynamicPanelConfig,
          fallbackPanelConfig,
        );
    }
  }

  public onLinkClick(
    href: string,
    creative: EcommercePromotionSource,
    position: number,
    link?: ILink,
  ) {
    this.componentStore.trackRedirection(href, creative, position, link);
  }
}
